"use client";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleOutlined,
} from "@ant-design/icons";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import { Card, Col, Form, Input, Row, Select, Tooltip } from "antd";

const sectionHeaderStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#475467",
  letterSpacing: "0.05em",
  marginBottom: 16,
};

const sectionDividerStyle = {
  borderTop: "1px dashed #e5e7eb",
  paddingTop: 24,
  marginTop: 24,
};

const metricCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 16,
};

const policyCheckItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "10px 0",
  fontSize: 14,
};

const defaultPolicyChecks = [
  {
    status: "pass",
    text: "KYC fully verified -- BVN, NIN and photo ID matched",
  },
  {
    status: "pass",
    text: "Credit score 742 exceeds minimum threshold of 600",
  },
  {
    status: "pass",
    text: "No active non-performing loans on record",
  },
  {
    status: "pass",
    text: "DSR 35.8% -- within the 50% maximum threshold",
  },
  {
    status: "warning",
    text: "Customer has 1 prior restructured loan (2023) -- note for credit committee",
  },
  {
    status: "pass",
    text: "AML / sanctions screening -- no match found",
  },
  {
    status: "pass",
    text: "Loan amount within product maximum (N50M)",
  },
  {
    status: "pass",
    text: "Customer account in good standing -- 0 overdraft events",
  },
];

const recommendationOptions = [
  { value: "approve", label: "Recommend approval" },
  { value: "decline", label: "Recommend decline" },
  { value: "refer", label: "Refer to committee" },
];

const riskClassificationOptions = [
  { value: "standard_low", label: "Standard -- low risk" },
  { value: "standard_medium", label: "Standard -- medium risk" },
  { value: "substandard", label: "Substandard" },
  { value: "doubtful", label: "Doubtful" },
];

const CreditAssessment = (props) => {
  const { onFinish, form, initialValues, customerData, loanData } = props;

  const creditScore = customerData?.creditScore ?? 742;
  const dsr = customerData?.dsr ?? 35.8;
  const ltv = loanData?.ltv ?? 58.8;
  const policyChecks = customerData?.policyChecks ?? defaultPolicyChecks;

  const scorePosition = ((creditScore - 300) / (850 - 300)) * 100;

  const getScoreLabel = (score) => {
    if (score >= 700) return { text: "GOOD -- Low risk", color: "#079455" };
    if (score >= 600) return { text: "FAIR -- Moderate risk", color: "#F59E0B" };
    return { text: "POOR -- High risk", color: "#DC2626" };
  };

  const scoreLabel = getScoreLabel(creditScore);

  return (
    <div>

      <div
        style={{
          background: "#F9FAFB",
          borderRadius: 12,
          padding: 16,
          marginBottom: 24,
          display: "flex",
          gap: 16,
        }}
      >
        <div style={{ flex: 1, border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "#475467",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              marginBottom: 6,
            }}
          >
            CREDIT BUREAU SCORE
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1.2,
              marginBottom: 2,
            }}
          >
            {creditScore}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: scoreLabel.color,
              marginBottom: 10,
            }}
          >
            {scoreLabel.text}
          </div>
          <div>
            <div
              style={{
                height: 6,
                borderRadius: 3,
                background:
                  "linear-gradient(to right, #DC2626, #F59E0B, #15CCAE, #079455)",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: `${scorePosition}%`,
                  top: -4,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  background: "#079455",
                  border: "2px solid white",
                  transform: "translateX(-50%)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 4,
                fontSize: 11,
                color: "#98A2B3",
              }}
            >
              <span>300</span>
              <span>Poor</span>
              <span>Fair</span>
              <span>Good</span>
              <span>850</span>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#475467",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              DEBT SERVICE RATIO
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#15CCAE",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {dsr}%
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "#475467",
              }}
            >
              <CheckCircleFilled style={{ color: "#079455", fontSize: 13 }} />
              <span>Policy max: 50% &middot; Within limit</span>
            </div>
          </div>

          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#475467",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              LOAN-TO-VALUE (LTV)
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#15CCAE",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {ltv}%
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "#475467",
              }}
            >
              <CheckCircleFilled style={{ color: "#079455", fontSize: 13 }} />
              <span>Policy max: 75% &middot; Within limit</span>
            </div>
          </div>
        </div>
      </div>

      <div style={sectionDividerStyle}>
        <div style={sectionHeaderStyle}>AUTOMATED POLICY CHECKS</div>
        <div>
          {policyChecks.map((item, index) => (
            <div key={index} style={policyCheckItemStyle}>
              {item.status === "pass" ? (
                <CheckCircleFilled
                  style={{ color: "#079455", fontSize: 16 }}
                />
              ) : (
                <ExclamationCircleFilled
                  style={{ color: "#DC6803", fontSize: 16 }}
                />
              )}
              <span
                style={{
                  color: item.status === "warning" ? "#93370D" : "#344054",
                }}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div style={sectionDividerStyle}>
        <div style={sectionHeaderStyle}>LOAN OFFICER RECOMMENDATION</div>

        <Form
          className="custom-form"
          layout="vertical"
          onFinish={onFinish}
          form={form}
          initialValues={initialValues}
          style={{ width: "100%" }}
        >
          <Row>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Recommendation
                    <Tooltip title="Select a recommendation">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="recommendation"
                rules={[
                  {
                    required: true,
                    message: "Please select a recommendation",
                  },
                ]}
              >
                <Select
                  placeholder="Select recommendation"
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" width={20} />}
                  options={recommendationOptions}
                />
              </Form.Item>
            </Col>
            <Col span={12} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Risk Classification
                    <Tooltip title="Select risk classification">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="riskClassification"
              >
                <Select
                  placeholder="Select risk classification"
                  style={{ height: 44 }}
                  suffixIcon={<ChevronDown color="#667085" width={20} />}
                  options={riskClassificationOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col span={24} style={{ padding: "0px 8px" }}>
              <Form.Item
                label={
                  <span>
                    Officer Notes / Conditions
                    <Tooltip title="Enter assessment notes and conditions">
                      <InfoCircleOutlined
                        style={{ marginLeft: "4px", color: "#667085" }}
                      />
                    </Tooltip>
                  </span>
                }
                name="officerNotes"
              >
                <Input.TextArea
                  className="bond-input"
                  style={{ height: 88, resize: "none" }}
                  placeholder="Enter assessment notes..."
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default CreditAssessment;
